// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2026 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import { mergeWithCss } from "@vanmeter/XDL/Utilities/mergeWithCss";
import React, { Children, FC, PropsWithChildren, useState } from "react";

export interface CartLineContainerProps {
    extendedStyles?: CartLineContainerStyles;
    showExtraCol?: boolean;
    [key: string]: any;
}
interface OwnProps {
    Image: FC<PropsWithChildren>;
    Description: FC<PropsWithChildren>;
    Price: FC<PropsWithChildren>;
    Qty: FC<PropsWithChildren>;
    ExtraCol: FC<PropsWithChildren>;
    Total: FC<PropsWithChildren>;
    Remove: FC<PropsWithChildren>;
    Actions: FC<PropsWithChildren>;
    Notes: FC<PropsWithChildren>;
}
export interface CartLineContainerStyles {
    container?: InjectableCss;
    image?: InjectableCss;
    description?: InjectableCss;
    price?: InjectableCss;
    qty?: InjectableCss;
    extraCol?: InjectableCss;
    total?: InjectableCss;
    actions?: InjectableCss;
    notes?: InjectableCss;
    remove?: InjectableCss;
}

export const cartlineContainerStyles: CartLineContainerStyles = {};

export const cartlineContainerStylesExtraCol: CartLineContainerStyles = {};

const names = {
    Image: "Image",
    Description: "Description",
    Price: "Price",
    Qty: "Qty",
    ExtraCol: "ExtraCol",
    Total: "Total",
    Remove: "Remove",
    Actions: "Actions",
    Notes: "Notes",
};

const Image: FC<PropsWithChildren> = () => null;
const Description: FC<PropsWithChildren> = () => null;
const Price: FC<PropsWithChildren> = () => null;
const Qty: FC<PropsWithChildren> = () => null;
const ExtraCol: FC<PropsWithChildren> = () => null;
const Total: FC<PropsWithChildren> = () => null;
const Remove: FC<PropsWithChildren> = () => null;
const Actions: FC<PropsWithChildren> = () => null;
const Notes: FC<PropsWithChildren> = () => null;

const CartLineContainer: React.FC<PropsWithChildren<CartLineContainerProps>> & OwnProps = ({
    children,
    extendedStyles,
    showExtraCol = false,
    ...otherProps
}) => {
    const [styles] = useState(() =>
        mergeWithCss(showExtraCol ? cartlineContainerStylesExtraCol : cartlineContainerStyles, extendedStyles),
    );

    const { Image, Description, Price, Qty, ExtraCol, Total, Remove, Actions, Notes } = React.useMemo(() => {
        const childrenArray = Children.toArray(children) as React.ReactElement<PropsWithChildren>[];
        const MappedChilds = childrenArray.reduce(
            (results, current) => {
                const currentName = (current.type as React.NamedExoticComponent<any>)?.displayName;
                if (currentName) {
                    results[currentName] = current;
                }
                return results;
            },
            {} as Record<string, React.ReactElement<PropsWithChildren>>,
        );
        const slots = Object.entries(names).reduce(
            (results, [key, value]) => {
                results[value] = MappedChilds[value] || null;
                return results;
            },
            {} as Record<string, React.ReactElement<PropsWithChildren> | null>,
        );

        return slots;
    }, [children]);

    return (
        <StyledWrapper {...styles.container} {...otherProps}>
            <StyledWrapper {...styles.image} className="image-section">
                {Image?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.description} className="description-section">
                {Description?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.price} className="price-section">
                {Price?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.qty} className="qty-section">
                {Qty?.props?.children}
            </StyledWrapper>
            {showExtraCol && <StyledWrapper {...styles.extraCol}>{ExtraCol?.props?.children}</StyledWrapper>}
            <StyledWrapper {...styles.total} className="total-section">
                {Total?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.remove} className="remove-section">
                {Remove?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.actions} className="actions-section">
                {Actions?.props?.children}
            </StyledWrapper>
            <StyledWrapper {...styles.notes} className="notes-section">
                {Notes?.props?.children}
            </StyledWrapper>
        </StyledWrapper>
    );
};

CartLineContainer.Image = Image;
CartLineContainer.Description = Description;
CartLineContainer.Price = Price;
CartLineContainer.Qty = Qty;
CartLineContainer.ExtraCol = ExtraCol;
CartLineContainer.Total = Total;
CartLineContainer.Remove = Remove;
CartLineContainer.Actions = Actions;
CartLineContainer.Notes = Notes;

Image.displayName = names.Image;
Description.displayName = names.Description;
Price.displayName = names.Price;
Qty.displayName = names.Qty;
ExtraCol.displayName = names.ExtraCol;
Total.displayName = names.Total;
Remove.displayName = names.Remove;
Actions.displayName = names.Actions;
Notes.displayName = names.Notes;

export default CartLineContainer;
